import React, { useState, useEffect } from 'react';
import { getCustomerTransactions } from '@cldf/scotiaweb-customer-information';
import { Column, Direction } from '@scotia/canvas-react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import style from './shortcuts.scss';
import { REQUIRED_TRANSACTIONS, showShortcuts, hasCheckingAccountStatementEnabled } from './shortcuts.customer.access';
import { getScotiawebMenuRendererUrl, getClientRut } from '../../services/service.parameters';

const ShortcutBody = ({ checkingAccountParamNumber, url, rut }) => {
  const props = JSON.stringify({
    variant: 'shortcuts',
    rut,
    menu: [
      {
        label: 'Transferir',
        type: 'ROUTE',
        icon: 'IconTransferIllustrative',
        target: '#mfe/mfe-transfers-thirdparties-web',
      },
      {
        label: 'Pagar cuentas',
        type: 'ROUTE',
        icon: 'IconBillPaymentPendingIllustrative',
        target: '#mfe/mfe-payments-web-cl',
      },
      {
        label: 'Estado de cuenta',
        type: 'ROUTE',
        icon: 'IconBillsIllustrative',
        target: `#mfe/mfe-accounts-balancesmovements-web/param/saldos--${checkingAccountParamNumber}`,
      },
    ],
    // eslint-disable-next-line arrow-body-style
    navigationResolver: 'shortcut-home',
  });

  return (
    <React.Fragment>
      <div className={style['shortcut-menu_container']}>
        <renderer-loader src={url} props={props} />
      </div>
    </React.Fragment>
  );
};

ShortcutBody.displayName = 'ShortcutBody';

ShortcutBody.propTypes = {
  /**
   * the checking account number to be redirected
   */
  checkingAccountParamNumber: PropTypes.number,
  url: PropTypes.string.isRequired,
  rut: PropTypes.string.isRequired,
};

ShortcutBody.defaultProps = {
  checkingAccountParamNumber: undefined,
};

const ShortcutsRenderer = ({ available, checkingAccountParamNumber, url, rut }) => (
  <div
    className={cx(style.shortcut_section, {
      [style['shortcut_section-opened']]: available,
    })}
  >
    {available && url && rut ? (
      <React.Fragment>
        <Column>
          <Direction>
            <hr className={cx(style.shortcut_section__divider, 'hide--xs hide--sm')} />
          </Direction>
          <Direction>
            <ShortcutBody checkingAccountParamNumber={checkingAccountParamNumber} url={url} rut={rut} />
          </Direction>
        </Column>
      </React.Fragment>
    ) : null}
  </div>
);

ShortcutsRenderer.displayName = 'ShortcutsRenderer';

ShortcutsRenderer.propTypes = {
  /**
   * the checking account number to be redirected
   */
  checkingAccountParamNumber: PropTypes.number,
  available: PropTypes.bool,
  rut: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
};

export const Shortcuts = ({ rut }) => {
  const [shortcutsAvailable, isShortcutAvailable] = useState(false);
  const [checkingAccountParamNumber, setCheckingAccountParamNumber] = useState(undefined);
  const [scotiawebMenuUrl, setScotiawebMenuUrl] = useState(null);
  const [clientRut, setClientRut] = useState(null);

  useEffect(() => {
    getCustomerTransactions({
      ignoreEmpties: true,
      equals: REQUIRED_TRANSACTIONS,
    })
      .then(data => {
        isShortcutAvailable(showShortcuts(data));
        if (hasCheckingAccountStatementEnabled(data)) {
          const prdEle = data.T00100.prd;
          setCheckingAccountParamNumber(typeof prdEle === 'string' ? prdEle : prdEle[0]);
        }
      })
      .catch(() => {
        isShortcutAvailable(false);
      });
  }, []);

  useEffect(() => {
    getScotiawebMenuRendererUrl()
      .then(_scotiawebMenuUrl => {
        setScotiawebMenuUrl(_scotiawebMenuUrl);
      })
      .catch(error => console.log(error));
  }, []);

  useEffect(() => {
    if (rut) {
      setClientRut(rut);
    } else {
      getClientRut()
        .then(_rut => {
          setClientRut(_rut);
        })
        .catch(error => console.log(error));
    }
  }, []);

  return (
    <ShortcutsRenderer
      checkingAccountParamNumber={checkingAccountParamNumber}
      available={shortcutsAvailable}
      url={scotiawebMenuUrl}
      rut={clientRut}
    />
  );
};

Shortcuts.displayName = 'Shortcuts';

Shortcuts.propTypes = {
  /**
   * the checking account number to be redirected
   */
  rut: PropTypes.string,
};
